import React, { useState } from 'react';
import PageShell, { SectionTitle } from './PageShell';
import SunModeToggle from '../components/SunModeToggle';
import { useAuth } from '../context/AuthContext';
import { PREREQUIS_LIST } from '../data/coursEnVoiture';
import { PREREQUIS_EXTRA } from '../data/coursEnVoitureQcm';
import { VISUAL_MAP } from '../components/PrerequisVisuals';
import PrerequisPresentMode from '../components/PrerequisPresentMode';
import {
  Wrench, GitBranch, Eye, Route, Crosshair, Users, GitFork, ScanEye, Gauge, Repeat,
  ChevronDown, ExternalLink, Target, CheckCircle2, AlertCircle, Lightbulb, BookOpen,
  ShieldCheck, ArrowRight, Lock, Presentation, Maximize2, AlertTriangle, Brain, HelpCircle,
} from 'lucide-react';

const ICON_MAP = { Wrench, GitBranch, Eye, Route, Crosshair, Users, GitFork, ScanEye, Gauge, Repeat };

const ITEMS = PREREQUIS_LIST.map((it) => ({ ...it, ...(PREREQUIS_EXTRA[it.slug] || {}) }));

const ACCENT_STYLES = {
  red: { border: 'border-red-500/40', bg: 'from-red-950/25 to-[#0d0d0e]', dot: 'bg-red-500', text: 'text-red-300', badge: 'bg-red-500/15 border-red-500/40 text-red-300' },
  amber: { border: 'border-amber-500/40', bg: 'from-amber-950/25 to-[#0d0d0e]', dot: 'bg-amber-500', text: 'text-amber-300', badge: 'bg-amber-500/15 border-amber-500/40 text-amber-300' },
  emerald: { border: 'border-emerald-500/40', bg: 'from-emerald-950/25 to-[#0d0d0e]', dot: 'bg-emerald-500', text: 'text-emerald-300', badge: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' },
  sky: { border: 'border-sky-500/40', bg: 'from-sky-950/25 to-[#0d0d0e]', dot: 'bg-sky-500', text: 'text-sky-300', badge: 'bg-sky-500/15 border-sky-500/40 text-sky-300' },
  violet: { border: 'border-violet-500/40', bg: 'from-violet-950/25 to-[#0d0d0e]', dot: 'bg-violet-500', text: 'text-violet-300', badge: 'bg-violet-500/15 border-violet-500/40 text-violet-300' },
};

function PrerequisCard({ item, expanded, onToggle, onPresent }) {
  const Icon = ICON_MAP[item.icon] || Route;
  const s = ACCENT_STYLES[item.accent] || ACCENT_STYLES.red;
  return (
    <div className={`rounded-3xl border ${s.border} bg-gradient-to-br ${s.bg} overflow-hidden transition`} data-testid={`prerequis-card-${item.slug}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-5 sm:p-6 flex items-start gap-4 hover:bg-white/[0.02] transition"
      >
        <div className={`shrink-0 w-14 h-14 rounded-2xl ${s.badge} border flex items-center justify-center`}>
          <Icon size={26} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10.5px] font-mono-spec uppercase tracking-widest font-bold ${s.text} mb-1 inline-flex items-center gap-1.5`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {item.kicker}
          </div>
          <h3 className="font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">{item.title}</h3>
          <p className="text-white/60 text-[13.5px] sm:text-[14.5px] mt-1.5 leading-relaxed">{item.tagline}</p>
        </div>
        <ChevronDown size={20} className={`shrink-0 text-white/50 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-white/10 p-5 sm:p-7 space-y-6" data-testid={`prerequis-detail-${item.slug}`}>
          {/* Boutons présentation */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onPresent('start')}
              data-testid={`prerequis-present-${item.slug}`}
              className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-bold inline-flex items-center gap-2 shadow-lg shadow-red-900/30"
            >
              <Maximize2 size={14} /> Présenter en plein écran
            </button>
            {item.qcm?.length > 0 && (
              <button
                onClick={() => onPresent('qcm')}
                data-testid={`prerequis-qcm-${item.slug}`}
                className={`h-11 px-5 rounded-full ${s.badge} border text-[13.5px] font-bold inline-flex items-center gap-2`}
              >
                <HelpCircle size={14} /> Mini QCM ({item.qcm.length} questions)
              </button>
            )}
          </div>

          {/* Visuel pédagogique — support d'échange avec l'élève */}
          {VISUAL_MAP[item.slug] && (
            <div data-testid={`prerequis-visual-${item.slug}`}>
              <div className={`text-[10.5px] uppercase tracking-[0.22em] font-mono-spec font-bold ${s.text} mb-2.5 inline-flex items-center gap-1.5`}>
                <Presentation size={12} /> Visuel à montrer à l&apos;élève
              </div>
              <div className={`rounded-2xl border ${s.border} overflow-hidden`}>
                {React.createElement(VISUAL_MAP[item.slug])}
              </div>
            </div>
          )}

          {/* Pourquoi */}
          <Block icon={Lightbulb} accent={s} title="Pourquoi c'est fondamental">
            <p className="text-white/85 text-[15.5px] leading-relaxed">{item.pourquoi}</p>
          </Block>

          {/* Comment faire */}
          <Block icon={Target} accent={s} title="Comment faire (gestes concrets)">
            <ul className="space-y-2">
              {item.comment.map((c, i) => (
                <li key={i} className="flex gap-3 text-white/85 text-[15px] leading-relaxed">
                  <span className={`shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${s.dot}`} />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </Block>

          {/* Risques */}
          {item.risques?.length > 0 && (
            <Block icon={AlertTriangle} accent={s} title="Risques si non maîtrisé">
              <ul className="space-y-2">
                {item.risques.map((r, i) => (
                  <li key={i} className="flex gap-3 text-white/85 text-[15px] leading-relaxed">
                    <span className="shrink-0 mt-1 text-red-400"><AlertTriangle size={13} /></span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Block>
          )}

          {/* Influences */}
          {item.influences?.length > 0 && (
            <Block icon={Brain} accent={s} title="Influences (internes & externes)">
              <div className="grid sm:grid-cols-2 gap-2">
                {item.influences.map((inf, i) => (
                  <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-white/80 text-[14.5px] leading-snug">
                    {inf}
                  </div>
                ))}
              </div>
            </Block>
          )}

          {/* Points-clés */}
          <Block icon={CheckCircle2} accent={s} title="Points-clés à retenir">
            <div className="grid sm:grid-cols-2 gap-2">
              {item.points_cles.map((p, i) => (
                <div key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-white/85 text-[14.5px] leading-snug">
                  {p}
                </div>
              ))}
            </div>
          </Block>

          {/* Erreurs classiques */}
          <Block icon={AlertCircle} accent={s} title="Erreurs classiques des élèves">
            <ul className="space-y-2">
              {item.erreurs.map((e, i) => (
                <li key={i} className="flex gap-3 text-white/75 text-[14.5px] leading-relaxed">
                  <span className="shrink-0 mt-1 text-red-400 font-bold">✗</span>
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </Block>

          {/* Exercices */}
          <Block icon={BookOpen} accent={s} title="Exercices à répéter en leçon">
            <ol className="space-y-2">
              {item.exercices.map((ex, i) => (
                <li key={i} className="flex gap-3 text-white/85 text-[14.5px] leading-relaxed">
                  <span className={`shrink-0 w-6 h-6 rounded-full ${s.badge} border text-[11px] font-mono-spec font-bold flex items-center justify-center`}>{i + 1}</span>
                  <span className="pt-0.5">{ex}</span>
                </li>
              ))}
            </ol>
          </Block>

          {/* Source officielle */}
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`prerequis-source-${item.slug}`}
              className={`inline-flex items-center gap-2 text-[12.5px] font-semibold ${s.text} bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-4 py-2 transition`}
            >
              <ExternalLink size={13} />
              Source officielle : {item.source_label}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function Block({ icon: Icon, accent, title, children }) {
  return (
    <div>
      <div className={`text-[10.5px] uppercase tracking-[0.22em] font-mono-spec font-bold ${accent.text} mb-2.5 inline-flex items-center gap-1.5`}>
        <Icon size={12} /> {title}
      </div>
      {children}
    </div>
  );
}

export default function CoursEnVoiturePage() {
  const { user, loading: authLoading } = useAuth();
  const [expanded, setExpanded] = useState(null);
  const [openAll, setOpenAll] = useState(false);
  const [presenting, setPresenting] = useState(null);

  const toggle = (slug) => {
    if (openAll) {
      setOpenAll(false);
      setExpanded(slug);
    } else {
      setExpanded((prev) => (prev === slug ? null : slug));
    }
  };

  if (authLoading) return <PageShell><div className="max-w-7xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;
  if (!user || user.role !== 'enseignant') {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto p-12 text-center" data-testid="cours-en-voiture-locked">
          <Lock className="mx-auto text-red-400 mb-4" size={32} />
          <h2 className="font-serif-editorial text-white text-[28px]">Réservé aux enseignants ECSR</h2>
          <p className="text-white/55 mt-2">Cet outil pédagogique (Top 10 pré-requis · Cours en voiture) est exclusivement accessible avec un compte <strong className="text-white">enseignant</strong>.</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <SunModeToggle />
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-6 sm:mt-10" data-testid="cours-en-voiture-page">
        {/* Hero */}
        <div className="mb-10 sm:mb-14">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono-spec font-bold text-red-400 mb-3 inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> Espace enseignant · Cours en voiture
          </div>
          <h1 className="font-serif-editorial text-white text-[36px] sm:text-[54px] lg:text-[64px] leading-[1.02] mb-4">
            Les 10 pré-requis<br />pour rouler en sécurité
          </h1>
          <p className="text-white/60 text-[15px] sm:text-[17px] leading-relaxed max-w-3xl">
            Les 10 compétences fondamentales sur lesquelles s&apos;articule TOUT l&apos;apprentissage de la conduite. Chaque fiche contient un <strong className="text-white">visuel</strong>, le pourquoi, le comment, les <strong className="text-white">risques et influences</strong>, et se termine par un <strong className="text-white">mini QCM d&apos;ancrage</strong>. Lance le mode plein écran pour présenter directement sur ton téléphone à l&apos;élève.
          </p>
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setOpenAll(!openAll); setExpanded(null); }}
              data-testid="prerequis-toggle-all"
              className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-[13px] font-semibold inline-flex items-center gap-2 transition"
            >
              {openAll ? <>Replier tout</> : <>Tout déplier <ArrowRight size={13} /></>}
            </button>
            <a
              href="https://www.securite-routiere.gouv.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 px-4 rounded-full border border-white/10 text-white/60 hover:text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition"
            >
              <ExternalLink size={12} /> Sources officielles Sécurité Routière
            </a>
          </div>
        </div>

        {/* Liste des 10 pré-requis */}
        <div className="space-y-4">
          {ITEMS.map((item) => (
            <PrerequisCard
              key={item.slug}
              item={item}
              expanded={openAll || expanded === item.slug}
              onToggle={() => toggle(item.slug)}
              onPresent={(startAt) => setPresenting({ item, startAt })}
            />
          ))}
        </div>

        {/* Footer info */}
        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6 text-center">
          <p className="text-white/70 text-[14px] leading-relaxed">
            Ces fiches sont un <strong className="text-white">complément pédagogique</strong>, pas un substitut à la conduite accompagnée par un enseignant qualifié. Basées sur le REMC et les recommandations de la Sécurité Routière française.
          </p>
        </div>

        {presenting && (
          <PrerequisPresentMode
            item={presenting.item}
            startAt={presenting.startAt}
            onClose={() => setPresenting(null)}
          />
        )}
      </section>
    </PageShell>
  );
}
